import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { DCUIContent, DCUITheme } from "@iddataweb/idw-common-ui/types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { ColorPickerFormGroup } from "../../../../../common/color-picker-form-group/color-picker-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingThemeFooterSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    dcuiContent: DCUIContent;
    dcuiTheme: DCUITheme;
    inheritedDCUITheme: DCUITheme;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
    updateDCUIThemePropertyValue: (value: DCUITheme) => void;
};

export const DCUIThemingThemeFooterSection = (props: DCUIThemingThemeFooterSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        dcuiContent,
        dcuiTheme,
        inheritedDCUIContent,
        inheritedDCUITheme,
        pageLevel,
        updateDCUIContentPropertyValue,
        updateDCUIThemePropertyValue
    } = props;

    const handleBackgroundColorChange = (newValue: string) => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            footer: {
                ...dcuiTheme.footer,
                backgroundColor: newValue
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleResetBackgroundColor = () => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            footer: {
                ...dcuiTheme.footer,
                backgroundColor: inheritedDCUITheme.footer.backgroundColor
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleFooterTextColorChange = (newValue: string) => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            footer: {
                ...dcuiTheme.footer,
                textColor: newValue
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleResetFooterTextColor = () => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            footer: {
                ...dcuiTheme.footer,
                textColor: inheritedDCUITheme.footer.textColor
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleFooterTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentPropertyValue({
            ...dcuiContent,
            footer: {
                ...dcuiContent.footer,
                text: event.target.value
            }
        });
    };

    const handleResetFooterText = () => {
        updateDCUIContentPropertyValue({
            ...dcuiContent,
            footer: {
                ...dcuiContent.footer,
                text: inheritedDCUIContent.footer.text
            }
        });
    };

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUITheme.footer.backgroundColor}
                currentThemingPropertyValue={dcuiTheme.footer.backgroundColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"dcui-theming-footer-background-color-picker"}
                    automationId={"dcui-theming-footer-background-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.footer.inputs.backgroundColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.footer.inputs.backgroundColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={dcuiTheme.footer.backgroundColor || ""}
                    onChange={handleBackgroundColorChange}
                    position={"bottom"}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.footer.inputs.backgroundColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetBackgroundColor}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUITheme.footer.textColor}
                currentThemingPropertyValue={dcuiTheme.footer.textColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"dcui-theming-footer-footer-text-color-picker"}
                    automationId={"dcui-theming-footer-footer-text-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.footer.inputs.footerTextColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.footer.inputs.footerTextColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={dcuiTheme.footer.textColor || ""}
                    onChange={handleFooterTextColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.footer.inputs.footerTextColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFooterTextColor}
                />

            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.footer.text}
                currentThemingPropertyValue={dcuiContent.footer.text}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"dcui-theming-footer-footer-text-input"}
                    automationId={"dcui-theming-footer-footer-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.footer.inputs.footerText.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.footer.inputs.footerText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleFooterTextChange}
                    value={dcuiContent.footer.text || ""}
                    disabled={isDisabled}
                    isRequired={false}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.footer.inputs.footerText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFooterText}
                />

            </ThemingFormGroupWrapper>

        </div>
    );
};